<template>
  <ul class="collapsible-list">
    <li v-for="(item, i) in items"
        :key="`collapsibleListItem-${i}`"
        class="collapsible-list-item"
        :class="openItems[i] ? 'open' : 'closed'"
    >
      <div class="w-full bg-transparent text-left py-4 pr-5 flex cursor-pointer font-semibold text-black"
           :class="{ 'bg-primary-light': selectedIndex === i && isSelected }"
      >
        <button class="collapsible-list-item-button cursor-pointer"
                @click="openItem(i)"
        >
          <span class="arrow-container">
            <vs-icon icon="arrow_drop_up"
                    size="1.4rem"
                    class="text-gray-dark arrow-container-icon"
            />
          </span>
        </button>
        <slot name="title" :item="item" class="font-semibold text-black py-4"/>
      </div>

      <transition
        name="expand"
        @before-enter="beforeEnter($event, i)"
        @enter="enter"
        @after-enter="afterEnter"
        @before-leave="beforeLeave"
        @leave="leave"
        @after-leave="afterLeave"
      >
        <div class="collapsible-list-item-content"
             v-show="openItems[i]"
        >
          <slot name="content" :item="item"/>
        </div>
      </transition>
    </li>
  </ul>
</template>

<script>
export default {
  name: 'CollapsibleList',

  props: {
    value: {
      type: Object,
      required: true
    },
    titleKey: {
      type: String,
      default: 'name'
    },
    items: {
      type: Array,
      required: true
    },
    selectedIndex: {
      type: Number,
      default: 0
    },
    isSelected: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {}
  },

  computed: {
    openItems: {
      get() {
        return this.value
      },
      set(i) {
        const newOpenItems = { ...this.value }
        newOpenItems[i] = !this.value[i]
        this.$emit('input', newOpenItems)
      }
    }
  },

  methods: {
    openItem(i) {
      const activeElement = document.activeElement
      if (activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable
      )) {
        return
      }

      this.openItems = i
    },

    beforeEnter(el, i) {
      if (!el.children.length) {
        el.style.border = 'none'
        this.openItems = i
      }
      el.style.maxHeight = '0px'
      el.style.paddingTop = '0px'
      el.style.paddingBottom = '0px'
    },

    enter(el) {
      el.style.maxHeight = `${el.scrollHeight}px`
    },

    afterEnter(el) {
      el.style.paddingTop = '1rem'
      el.style.paddingBottom = '1rem'
      el.style.maxHeight = null
      el.style.paddingTop = null
      el.style.paddingBottom = null
    },

    beforeLeave(el) {
      el.style.paddingTop = '1rem'
      el.style.paddingBottom = '1rem'
      el.style.maxHeight = `${el.scrollHeight}px`
    },

    leave(el) {
      el.offsetHeight
      el.style.paddingTop = '0px'
      el.style.paddingBottom = '0px'
      el.style.maxHeight = '0px'
    },

    afterLeave(el) {
      el.style.maxHeight = null
      el.style.paddingTop = null
      el.style.paddingBottom = null
    }
  }
}
</script>

<style scoped lang="scss">
.collapsible-list {
  @apply gap-6 flex flex-col;
}

.collapsible-list-item {
  @apply rounded-lg border-solid border border-gray;

  &.closed .arrow-container-icon {
    transform: rotate(90deg);
  }

  &.open .arrow-container-icon {
    transform: rotate(180deg);
  }

  &.open .collapsible-list-item-content {
    @apply border-0 border-t border-solid border-soft;
  }
}

.collapsible-list-item-button {
  @apply bg-transparent border-0;
  @apply flex items-center pl-3;
  @apply border-0;

  .arrow-container {
    @apply inline-flex mr-2;
    transition: transform 0.3s ease-in-out;

    &.closed {
      transform: rotate(90deg);
    }

    &.open {
      transform: rotate(180deg);
    }
  }
}

.collapsible-list-item-content {
  overflow: hidden;
  transition: max-height 0.3s ease-in-out, padding-top 0.3s ease-in-out, padding-bottom 0.3s ease-in-out;
}
</style>
